<!--
  Daily Report.

  Header (project, weather, date, ticket #), then three independent child
  tables in printed order — personnel, deliveries, activities — then photos,
  notes and prepared-by/reviewed-by. Follows MixDesignForm.svelte's shape: raw
  string state, add/remove/reorder rows per table, delete behind an in-page
  confirmation banner rather than confirm(). Every field here is already a
  plain string on the wire, so there is no toNumber/toInput round trip to
  worry about.

  AUTO-SAVE: this form saves itself, ~2 s after the last change (the explicit
  Save button stays for the person who wants to be sure). A loaded report
  saves any change; a NEW form first creates itself only once it holds real
  content — weather, a date, a person, a delivery amount, an activity, notes
  — never on the site/project prefill alone, so backing out of "New daily
  report for Kits" leaves nothing behind and burns no ticket number. Adding a
  photo counts as deliberate content and saves the report first, because a
  photo needs a report id to attach to.

  PHOTOS: captured or picked inline, downscaled on-device (lib/photo.ts's
  downscalePhoto — same routine the pour record uses), then sent as ONE
  authenticated request whose body is the image. No offline queue here: this
  is an office/site-office screen, and the server's content-hash key makes a
  retried upload collapse onto the same photo rather than duplicate.

  Quick entry: the four clock fields and Amount supplied normalise what was
  typed when the field is left — '330' becomes '3:30 PM', '12.3' becomes
  '12.3 m3' (src/lib/calc/quick-entry.ts). Only a bare entry is touched;
  anything already formatted is kept exactly as typed.

  A brand-new report seeds personnel (Khole, Dennis, Timour, Kenny) and
  deliveries (Sand, Gravel, Cement, one Admixture row) — mirrored here on
  the client from the same DAILY_REPORT_DEFAULT_* lists the server would seed
  from if these keys were left out of the save body, so the user sees (and
  can edit) the standard rows before ever saving. Activities start with no
  rows either way: the paper's blank activity rows are just space, not
  content.

  A delivery's material is picked from a list — the priced materials, the
  seeded names, and whatever the rows already hold — because the P&L matches a
  delivery to a price by folded name, so a typo used to produce an unpriced
  line in silence. "Something else…" reveals a text box for that row: an admix
  nobody has set up yet must still be recordable on site.

  The activity table is the widest thing in this feature (7 fields per row).
  It uses the same one-line-at-60rem / stacked-below-60rem approach as the
  other tables here and as MixDesignForm's materials grid, with narrower
  per-column floors sized so the whole row — even 7 fields plus the 3-button
  action column — fits inside the 64rem office container without ever
  scrolling sideways.

  No acceptance verdicts: this form records what happened, not a judgement.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { apiPath } from '../../lib/api-contract';
  import { createAutosave } from '../../lib/autosave';
  import { printDailyReport } from '../../lib/export/quality-pdf';
  import SitePicker from './SitePicker.svelte';
  import { claimAddress, linkHandler, location as appLocation, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { DAILY_REPORT_DEFAULT_DELIVERIES } from '../../lib/seed-data';
  import { advanceOnEnter } from '../../lib/fields/advance-on-enter';
  import { DEFAULT_OTHER_ACTIVITIES, foldName } from '../../lib/calc/pnl';
  import { downscalePhoto } from '../../lib/photo';
  import { quickCubes, quickTime, quickTonnes } from '../../lib/calc/quick-entry';
  import { totalCubes } from '../../lib/calc/pnl';
  import { cubes } from '../../lib/money';
  import { isAdmin, notify, notifyError } from '../../lib/store';
  import type {
    DailyReportActivityInput,
    DailyReportDeliveryInput,
    DailyReportOtherActivityInput,
    DailyReportPersonInput,
    DailyReportPhoto,
    MixDesignOption,
    SaveDailyReportRequest,
    SiteSummary
  } from '../../lib/types';

  interface Props {
    /** null for a new report. */
    dailyReportId?: number | null;
  }

  let { dailyReportId = null }: Props = $props();

  let loading = $state(dailyReportId !== null);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  /** The id once saved; a new report gets one on its first save. */
  let savedId = $state<number | null>(dailyReportId);

  let siteId = $state<number | null>(null);
  let project = $state('');
  let weather = $state('');
  let reportDate = $state('');
  let ticketNo = $state('');
  let notes = $state('');
  let preparedBy = $state('');
  let reviewedBy = $state('');

  function startingDeliveries(): DailyReportDeliveryInput[] {
    return DAILY_REPORT_DEFAULT_DELIVERIES.map((material) => ({ material, amount: '' }));
  }

  // Personnel starts EMPTY, on a new report as much as an existing one. It
  // used to seed four names; the crew filled times for whoever actually
  // worked and left the rest, and every leftover saved as a name with no
  // clock-in that the admin area could not price.
  let personnel = $state<DailyReportPersonInput[]>([]);
  let deliveries = $state<DailyReportDeliveryInput[]>(
    dailyReportId === null ? startingDeliveries() : []
  );
  let activities = $state<DailyReportActivityInput[]>([]);
  /** Standby, Delivery, Small load fee, ...: which and how many. Priced by admin, never here. */
  let otherActivities = $state<DailyReportOtherActivityInput[]>([]);

  /** The Other activities pick-list with units; never a rate. Starts with the standard three. */
  let activityChoices = $state<{ name: string; unit: string }[]>(
    DEFAULT_OTHER_ACTIVITIES.map((d) => ({ name: d.name, unit: d.unit }))
  );

  /** The mix design library as a pick-list; loaded once, a convenience only. */
  let mixOptions = $state<MixDesignOption[]>([]);

  /** The materials that carry a price, names only — never a cost. */
  let pricedMaterials = $state<string[]>([]);

  /** The active roster, for the personnel pick-list. Names only — this screen
   *  is never told what anyone is paid. */
  let rosterNames = $state<string[]>([]);

  $effect(() => {
    api
      .listMixDesignOptions()
      .then((response) => {
        mixOptions = response.mix_designs;
      })
      .catch(() => {
        // The typed code still saves and prints; only the pick-list is missing.
      });
  });

  $effect(() => {
    api
      .listPersonnelOptions()
      .then((response) => {
        rosterNames = response.names;
      })
      .catch(() => {
        // Same bargain as the material list: "Someone else…" still takes any
        // name, so a field crew is never blocked from recording who worked
        // because an admin list would not load.
      });
  });

  $effect(() => {
    // Re-read whenever the site changes: a site can carry materials of its own.
    const site = siteId;
    api
      .listActivityOptions(site)
      .then((response) => {
        activityChoices = response.activities;
      })
      .catch(() => {
        // The standard three stay offered; "Something else..." takes anything.
      });
  });

  $effect(() => {
    const site = siteId;
    api
      .listMaterialOptions(site)
      .then((response) => {
        pricedMaterials = response.materials;
      })
      .catch(() => {
        // Same bargain as the mix list: the seeded names and whatever the rows
        // already hold still fill the pick-list, and "Something else…" still
        // takes anything. A field crew is never blocked from recording a
        // delivery because an admin list would not load.
      });
  });

  /**
   * Choosing a mix sets BOTH fields: the id for the admin area's pricing and
   * the code into the text column, so the printed sheet reads "SC01".
   * Choosing the blank option only clears the id — it leaves water_used
   * alone, so a row typed as e.g. "CC520N" before the pick-list existed
   * keeps that text on the printed sheet.
   */
  function chooseMix(index: number, value: string) {
    if (value === '') {
      activities[index].mix_design_id = null;
      return;
    }
    const option = mixOptions.find((m) => m.id === Number(value)) ?? null;
    activities[index].mix_design_id = option ? option.id : null;
    activities[index].water_used = option ? option.mix_code : '';
  }

  $effect(() => {
    if (dailyReportId === null) return;
    loading = true;
    api
      .getDailyReport(dailyReportId)
      .then((response) => {
        const report = response.daily_report;
        siteId = report.site_id;
        project = report.project;
        weather = report.weather;
        reportDate = report.report_date;
        ticketNo = report.ticket_no;
        notes = report.notes;
        preparedBy = report.prepared_by;
        reviewedBy = report.reviewed_by;
        personnel = response.personnel.map((row) => ({
          name: row.name,
          time_in: row.time_in,
          time_out: row.time_out
        }));
        // A saved name is a pick-list entry, whatever it says: the loaded rows
        // seed the options, so even one typed before the roster existed comes
        // back selected rather than blank.
        customPerson = [];
        deliveries = response.deliveries.map((row) => ({
          material: row.material,
          amount: row.amount
        }));
        // A saved material is a pick-list entry, whatever it says: the loaded
        // rows seed the options, so even one typed before the list existed
        // comes back selected rather than blank.
        customDelivery = [];
        activities = response.activities.map((row) => ({
          piling_number: row.piling_number,
          unit_number: row.unit_number,
          start_time: row.start_time,
          stop_time: row.stop_time,
          amount_supplied: row.amount_supplied,
          admixtures_used: row.admixtures_used,
          water_used: row.water_used,
          mix_design_id: row.mix_design_id
        }));
        otherActivities = (response.other_activities ?? []).map((row) => ({
          activity: row.activity,
          quantity: row.quantity
        }));
        customOther = [];
        savedId = report.id;
        // The loaded document is the auto-save baseline: nothing is dirty yet.
        lastSaved = JSON.stringify(body());
      })
      .catch((cause) => {
        notifyError(cause, 'Could not load that daily report');
        error = 'Could not load that daily report.';
      })
      .finally(() => {
        loading = false;
      });
  });

  // Photos already attached, loaded whenever the report has an id (a new
  // report gets one on its first save — auto or manual — and then loads an
  // empty list).
  let photos = $state<DailyReportPhoto[]>([]);
  $effect(() => {
    const id = savedId;
    if (id === null) return;
    api
      .listDailyReportPhotos(id)
      .then((response) => {
        photos = response.photos;
      })
      .catch(() => {
        // The list is re-fetched on the next save; a failed load never blocks
        // the form.
      });
  });

  // --- personnel -----------------------------------------------------------

  function addPerson() {
    customPerson = [...personFlags(), false];
    personnel = [...personnel, { name: '', time_in: '', time_out: '' }];
  }

  function removePerson(index: number) {
    customPerson = personFlags().filter((_, i) => i !== index);
    personnel = personnel.filter((_, i) => i !== index);
  }

  function movePerson(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= personnel.length) return;
    const next = [...personnel];
    [next[index], next[target]] = [next[target], next[index]];
    personnel = next;
    // The free-text flag belongs to the row, so it travels with it.
    const flags = personFlags();
    [flags[index], flags[target]] = [flags[target], flags[index]];
    customPerson = flags;
  }

  /**
   * The value the "Someone else…" option carries. Each listed name is its
   * INDEX in `personChoices`, never the name itself, so no name — however it
   * is spelled — can collide with this sentinel or with the blank option.
   */
  const OTHER_PERSON = 'other';

  /** Rows showing the free-text box instead of the pick-list, by row index. */
  let customPerson = $state<boolean[]>([]);

  function personFlags(): boolean[] {
    return Array.from({ length: personnel.length }, (_, i) => customPerson[i] === true);
  }

  /**
   * The roster, plus any name already on a row, so a report saved before this
   * existed never blanks a name that was typed by hand. Folded
   * case-insensitively, first spelling wins — the same key the admin area
   * matches a name to a rate on.
   *
   * A row being typed into is deliberately left out: its half-finished text
   * has no business appearing in every other row's dropdown.
   */
  const personChoices = $derived.by(() => {
    const onRows = personnel.filter((_, i) => !customPerson[i]).map((row) => row.name);
    const seen = new Set<string>();
    const names: string[] = [];
    for (const name of [...rosterNames, ...onRows]) {
      const key = foldName(name);
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
    return names;
  });

  /**
   * Which option a row shows. Matched on the folded name so a row saved as
   * 'khole' still displays as Khole rather than falling back to blank — the
   * stored text is left exactly as it was until the user picks something.
   */
  function personValue(index: number): string {
    if (customPerson[index]) return OTHER_PERSON;
    const key = foldName(personnel[index].name);
    if (key === '') return '';
    const at = personChoices.findIndex((name) => foldName(name) === key);
    return at === -1 ? '' : String(at);
  }

  /**
   * Picking "Someone else…" reveals the box with the current text still in
   * it: a mis-tap must not destroy what was already recorded.
   */
  function choosePerson(index: number, value: string) {
    const flags = personFlags();
    flags[index] = value === OTHER_PERSON;
    customPerson = flags;
    if (value === OTHER_PERSON) return;
    personnel[index].name = value === '' ? '' : (personChoices[Number(value)] ?? '');
  }

  // --- deliveries ------------------------------------------------------------

  /**
   * The value the "Something else…" option carries. Each listed material is
   * its INDEX in `materialChoices`, never the name itself, so no material —
   * however an admin spells one — can collide with this sentinel or with the
   * blank option.
   */
  const OTHER_MATERIAL = 'other';

  /** Rows showing the free-text box instead of the pick-list, by row index. */
  let customDelivery = $state<boolean[]>([]);

  /**
   * The flags as a DENSE array one per delivery row. Every edit goes through
   * this: a sparse array would have Array#filter skip its holes and slide the
   * remaining flags onto the wrong rows.
   */
  function deliveryFlags(): boolean[] {
    return Array.from({ length: deliveries.length }, (_, i) => customDelivery[i] === true);
  }

  /**
   * The pick-list: the seeded names first (what the paper form prints, and
   * what nearly every row is), then the rest of the priced materials, then
   * whatever the rows themselves already hold — so opening a report written
   * before this existed never blanks or rewrites a material that was typed by
   * hand. Folded case-insensitively, first spelling wins, which is the same
   * key the P&L matches a delivery to a price on.
   *
   * A row being typed into is deliberately left out: its half-finished text
   * has no business appearing in every other row's dropdown.
   */
  const materialChoices = $derived.by(() => {
    const onRows = deliveries.filter((_, i) => !customDelivery[i]).map((row) => row.material);
    const seen = new Set<string>();
    const names: string[] = [];
    for (const name of [...DAILY_REPORT_DEFAULT_DELIVERIES, ...pricedMaterials, ...onRows]) {
      const key = foldName(name);
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
    return names;
  });

  /**
   * Which option a row shows. Matched on the folded name so a row saved as
   * 'sand' still displays as Sand rather than falling back to blank — the
   * stored text is left exactly as it was until the user picks something.
   */
  function materialValue(index: number): string {
    if (customDelivery[index]) return OTHER_MATERIAL;
    const key = foldName(deliveries[index].material);
    if (key === '') return '';
    const at = materialChoices.findIndex((name) => foldName(name) === key);
    return at === -1 ? '' : String(at);
  }

  /**
   * Picking "Something else…" reveals the box with the current text still in
   * it: a mis-tap must not destroy what was already recorded.
   */
  function chooseMaterial(index: number, value: string) {
    const flags = deliveryFlags();
    flags[index] = value === OTHER_MATERIAL;
    customDelivery = flags;
    if (value === OTHER_MATERIAL) return;
    deliveries[index].material = value === '' ? '' : (materialChoices[Number(value)] ?? '');
  }

  function addDelivery() {
    customDelivery = [...deliveryFlags(), false];
    deliveries = [...deliveries, { material: '', amount: '' }];
  }

  function removeDelivery(index: number) {
    customDelivery = deliveryFlags().filter((_, i) => i !== index);
    deliveries = deliveries.filter((_, i) => i !== index);
  }

  function moveDelivery(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= deliveries.length) return;
    const next = [...deliveries];
    [next[index], next[target]] = [next[target], next[index]];
    deliveries = next;
    // The free-text flag belongs to the row, so it travels with it.
    const flags = deliveryFlags();
    [flags[index], flags[target]] = [flags[target], flags[index]];
    customDelivery = flags;
  }

  // --- activities ------------------------------------------------------------

  function blankActivity(): DailyReportActivityInput {
    return {
      piling_number: '',
      unit_number: '',
      start_time: '',
      stop_time: '',
      amount_supplied: '',
      admixtures_used: '',
      water_used: '',
      mix_design_id: null
    };
  }

  function addActivity() {
    activities = [...activities, blankActivity()];
  }

  function removeActivity(index: number) {
    activities = activities.filter((_, i) => i !== index);
  }

  function moveActivity(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= activities.length) return;
    const next = [...activities];
    [next[index], next[target]] = [next[target], next[index]];
    activities = next;
  }

  // --- other activities ------------------------------------------------------

  const OTHER_ACTIVITY = 'other';
  /** Rows showing the free-text box instead of the pick-list, by row index. */
  let customOther = $state<boolean[]>([]);

  function otherFlags(): boolean[] {
    return Array.from({ length: otherActivities.length }, (_, i) => customOther[i] === true);
  }

  /** The list plus whatever a row already holds, folded, first spelling wins. */
  const otherChoices = $derived.by(() => {
    const seen = new Set<string>();
    const out: { name: string; unit: string }[] = [];
    const onRows = otherActivities.filter((_, i) => !customOther[i]).map((r) => ({ name: r.activity, unit: '' }));
    for (const option of [...activityChoices, ...onRows]) {
      const key = foldName(option.name);
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      out.push(option);
    }
    return out;
  });

  function otherValue(index: number): string {
    if (customOther[index]) return OTHER_ACTIVITY;
    const key = foldName(otherActivities[index].activity);
    if (key === '') return '';
    const at = otherChoices.findIndex((o) => foldName(o.name) === key);
    return at === -1 ? '' : String(at);
  }

  function chooseOther(index: number, value: string) {
    const flags = otherFlags();
    flags[index] = value === OTHER_ACTIVITY;
    customOther = flags;
    if (value === OTHER_ACTIVITY) return;
    otherActivities[index].activity = value === '' ? '' : (otherChoices[Number(value)]?.name ?? '');
  }

  /** "How many" in words the crew reads: hours for Standby, loads for Delivery. */
  function otherUnitHint(index: number): string {
    const key = foldName(otherActivities[index].activity);
    const unit = otherChoices.find((o) => foldName(o.name) === key)?.unit ?? '';
    return unit === 'hour' ? 'Hours' : unit === 'load' ? 'Loads' : 'How many';
  }

  function addOther() {
    customOther = [...otherFlags(), false];
    otherActivities = [...otherActivities, { activity: '', quantity: '' }];
  }

  function removeOther(index: number) {
    customOther = otherFlags().filter((_, i) => i !== index);
    otherActivities = otherActivities.filter((_, i) => i !== index);
  }

  function body(): SaveDailyReportRequest {
    return {
      site_id: siteId,
      project,
      weather,
      report_date: reportDate,
      ticket_no: ticketNo,
      notes,
      prepared_by: preparedBy,
      reviewed_by: reviewedBy,
      personnel: personnel.map((row) => ({ ...row })),
      deliveries: deliveries.map((row) => ({ ...row })),
      activities: activities.map((row) => ({ ...row })),
      other_activities: otherActivities.map((row) => ({ ...row }))
    };
  }

  function chooseSite(site: SiteSummary | null) {
    siteId = site?.id ?? null;
    if (site) project = site.name;
  }

  // Opened from a site page ("New daily report for Kits"): pre-select that
  // site and fill the project from it. Only on a NEW form, only when still
  // empty. Nothing else carries over — the date, ticket, weather and every
  // row are what makes one day's report different from the last.
  $effect(() => {
    const fromSite = siteIdFromQuery($appLocation.query);
    if (dailyReportId === null && fromSite !== null && siteId === null) {
      siteId = fromSite;
      api
        .getSite(fromSite)
        .then((response) => {
          if (!project.trim()) project = response.site.name;
        })
        .catch(() => {
          // The typed project text still saves even if the name lookup fails.
        });
    }
  });

  let printing = $state(false);

  /**
   * Print what is on screen, not what was last saved: the author expects the
   * document to match the form in front of them. Ids and timestamps are
   * placeholders because the builder does not print them.
   */
  function print() {
    if (printing) return;
    printing = true;
    const stamp = new Date().toISOString();
    const started = printDailyReport(
      {
        daily_report: {
          id: savedId ?? 0,
          project,
          site_id: siteId,
          weather,
          report_date: reportDate,
          ticket_no: ticketNo,
          notes,
          prepared_by: preparedBy,
          reviewed_by: reviewedBy,
          created_at: stamp,
          updated_at: stamp
        },
        personnel: personnel.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          ...row
        })),
        deliveries: deliveries.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          ...row
        })),
        activities: activities.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          ...row,
          mix_design_id: row.mix_design_id ?? null
        })),
        other_activities: otherActivities.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          ...row
        })),
        exported_at: stamp
      },
      {
        onDone: (cause) => {
          printing = false;
          if (cause) {
            notify({
              tone: 'warn',
              title: 'Could not open the print view',
              detail: 'Try again, or use your browser’s own Print command.',
              sticky: true
            });
          }
        }
      }
    );

    if (!started) {
      printing = false;
      notify({
        tone: 'warn',
        title: 'This browser will not print',
        detail: 'Use your browser’s own Print command instead.',
        sticky: true
      });
    }
  }

  async function save(opts: { auto?: boolean } = {}) {
    if (saving || removed) return;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      const snapshot = body();
      const response =
        savedId === null
          ? await api.createDailyReport(snapshot)
          : await api.updateDailyReport(savedId, snapshot);

      const isNew = savedId === null;
      savedId = response.daily_report.id;
      // The server hands a new report its running ticket number.
      ticketNo = response.daily_report.ticket_no;
      // The baseline is what the form NOW holds (ticket number included), so
      // the server filling the ticket in does not read as a fresh edit.
      lastSaved = JSON.stringify(body());
      if (!opts.auto) {
        notify({ tone: 'ok', title: isNew ? 'Daily report created' : 'Daily report saved' });
      }
      // A new report gets its own address, so a refresh or a back button
      // lands on the saved sheet rather than a blank one. An AUTO save only
      // rewrites the address bar: `navigate` would change the route param and
      // the {#key} around this screen would tear the form down mid-keystroke.
      if (isNew) {
        if (opts.auto) claimAddress(routePath.qualityDailyReport(savedId));
        else navigate(routePath.qualityDailyReport(savedId), { replace: true, keepScroll: true });
      }
    } catch (cause) {
      if (isApiError(cause)) {
        error = cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not save. Check the connection and try again.';
      }
    } finally {
      saving = false;
    }
  }

  /* --- auto-save ----------------------------------------------------------
   * ~2 s after the last change the form saves itself. `lastSaved` is the
   * JSON of the document as last written (or loaded); the effect below reads
   * the whole document — which is what subscribes it to every field — and
   * pokes the shared timer (lib/autosave.ts) only when the two differ.
   */

  /** JSON of the document at its last save/load — the dirty check's anchor. */
  let lastSaved = $state<string | null>(null);
  /** Set once the report is deleted, so no timer can quietly resurrect it. */
  let removed = false;

  /**
   * Does a NEW form hold something worth creating a report (and burning a
   * ticket number) for? The site/project prefill and the seeded delivery
   * NAMES deliberately do not count — only something a person typed.
   */
  function hasContent(): boolean {
    return (
      weather.trim() !== '' ||
      reportDate.trim() !== '' ||
      notes.trim() !== '' ||
      preparedBy.trim() !== '' ||
      reviewedBy.trim() !== '' ||
      personnel.some((p) => p.name.trim() !== '' || p.time_in.trim() !== '' || p.time_out.trim() !== '') ||
      activities.length > 0 ||
      otherActivities.length > 0 ||
      deliveries.some((d) => d.amount.trim() !== '') ||
      deliveries.map((d) => d.material).join(' ') !== DAILY_REPORT_DEFAULT_DELIVERIES.join(' ')
    );
  }

  const dirty = $derived(lastSaved !== null && JSON.stringify(body()) !== lastSaved);

  const autosave = createAutosave({
    snapshot: () => JSON.stringify(body()),
    saved: () => lastSaved,
    busy: () => saving || deleting,
    blocked: () => removed,
    ready: () => savedId !== null || hasContent(),
    save: () => void save({ auto: true })
  });

  $effect(() => {
    const snapshot = JSON.stringify(body()); // subscribes to every field
    if (loading) return;
    if (snapshot === lastSaved) return;
    autosave.changed();
  });

  // A timer must not outlive the form (navigation away mid-edit).
  $effect(() => {
    return () => autosave.stop();
  });

  /* --- photos ------------------------------------------------------------- */

  let photoBusy = $state(false);
  let photoProblem = $state<string | null>(null);
  /** The photo whose Remove is awaiting an inline confirmation, or null. */
  let confirmingPhotoRemove = $state<number | null>(null);

  async function onPhotoFiles(event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    // Clear immediately so picking the same file twice still fires a change.
    target.value = '';
    if (files.length === 0) return;

    photoBusy = true;
    photoProblem = null;
    try {
      // A photo attaches to a report id, so a new form saves itself first —
      // adding a photo is as deliberate as typing the weather.
      if (savedId === null) {
        await save({ auto: true });
        if (savedId === null) {
          photoProblem = 'The report could not be saved, so the photo has nowhere to attach. Try again.';
          return;
        }
      }
      for (const file of files) {
        try {
          const downscaled = await downscalePhoto(file);
          const response = await api.uploadDailyReportPhoto(savedId, downscaled.blob);
          // Idempotent server: the same bytes twice return the same row.
          if (!photos.some((p) => p.id === response.photo.id)) {
            photos = [response.photo, ...photos];
          }
        } catch (cause) {
          photoProblem = cause instanceof Error ? cause.message : 'That photo could not be added.';
        }
      }
    } finally {
      photoBusy = false;
    }
  }

  async function removePhoto(photoId: number) {
    confirmingPhotoRemove = null;
    try {
      await api.deleteDailyReportPhoto(photoId);
      photos = photos.filter((p) => p.id !== photoId);
    } catch (cause) {
      notifyError(cause, 'Could not remove that photo');
    }
  }

  /**
   * Delete, behind an in-page confirmation step rather than a confirm() —
   * the app uses no modal dialogs anywhere.
   */
  let confirmingDelete = $state(false);

  async function remove() {
    if (savedId === null || deleting) return;
    deleting = true;
    try {
      await api.deleteDailyReport(savedId);
      // No auto-save timer may quietly resurrect what was just deleted.
      removed = true;
      autosave.stop();
      notify({ tone: 'ok', title: 'Daily report deleted' });
      navigate(routePath.qualityDailyReportList(siteId ?? undefined), { replace: true });
    } catch (cause) {
      notifyError(cause, 'Could not delete that daily report');
    } finally {
      deleting = false;
      confirmingDelete = false;
    }
  }
</script>

<!-- Enter moves to the next field; see lib/fields/advance-on-enter.ts. -->
<div use:advanceOnEnter>
{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  {#if error}
    <div class="banner banner--error" role="alert">
      <div><strong>Could not save</strong>{error}</div>
    </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <div class="titlebar">
        <h1 class="card__title">{savedId === null ? 'New daily report' : project || 'Daily report'}</h1>
        <a class="btn btn--sm" href={routePath.qualityDailyReportList(siteId ?? undefined)} onclick={linkHandler}>
          All daily reports
        </a>
      </div>

      <SitePicker value={siteId} onchoose={chooseSite} />

      <label class="field">
        <span class="label">Project</span>
        <!--
          Prints on the sheet. Choosing a site above fills it in; it stays
          free text so a record from before sites, or a site not on the list
          yet, is never blocked or overwritten against the user's wishes.
        -->
        <input
          type="text"
          bind:value={project}
          placeholder="Project name"
        />
        <span class="field__hint">Filled in from the site; edit only if the sheet should say something different.</span>
        {#if fieldErrors.project}<span class="field__flag">{fieldErrors.project}</span>{/if}
      </label>

      <label class="field">
        <span class="label">Weather</span>
        <input type="text" bind:value={weather} placeholder="Sunny, 15°C" />
      </label>

      <div class="grid2">
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={reportDate} />
        </label>
        <label class="field">
          <span class="label">Ticket #</span>
          <input type="text" bind:value={ticketNo} readonly={savedId === null} placeholder={savedId === null ? 'Next number, on save' : ''} />
        </label>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Personnel</h2>

      <div class="rows rows--person">
        <div class="rows__head" aria-hidden="true">
          <span>Name</span>
          <span>Time in</span>
          <span>Time out</span>
          <span></span>
        </div>

        {#each personnel as person, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Name</span>
                <!--
                  Picked, not typed, so the admin area's rate match cannot be
                  defeated by a spelling. "Someone else…" is the escape hatch:
                  a new hire or a sub nobody has set up yet still goes on the
                  sheet, and shows up in Rates needing a dollar amount.
                -->
                <select
                  value={personValue(index)}
                  onchange={(event) => choosePerson(index, (event.currentTarget as HTMLSelectElement).value)}
                  aria-label="Person {index + 1} name"
                >
                  <option value="">—</option>
                  {#each personChoices as name, choice (name)}
                    <option value={String(choice)}>{name}</option>
                  {/each}
                  <option value={OTHER_PERSON}>Someone else…</option>
                </select>
                {#if customPerson[index]}
                  <input
                    type="text"
                    bind:value={person.name}
                    placeholder="Name"
                    aria-label="Person {index + 1} typed name"
                  />
                {/if}
              </label>
              <label class="row__field">
                <span class="label">Time in</span>
                <input
                  type="text"
                  bind:value={person.time_in}
                  onblur={() => (person.time_in = quickTime(person.time_in))}
                  placeholder="7:00 AM"
                  aria-label="Person {index + 1} time in"
                />
              </label>
              <label class="row__field">
                <span class="label">Time out</span>
                <input
                  type="text"
                  bind:value={person.time_out}
                  onblur={() => (person.time_out = quickTime(person.time_out))}
                  placeholder="3:30 PM"
                  aria-label="Person {index + 1} time out"
                />
              </label>
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => movePerson(index, -1)}
                disabled={index === 0}
                aria-label="Move {person.name || `person ${index + 1}`} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => movePerson(index, 1)}
                disabled={index === personnel.length - 1}
                aria-label="Move {person.name || `person ${index + 1}`} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removePerson(index)}
                aria-label="Remove {person.name || `person ${index + 1}`}"
              >✕</button>
            </div>
          </div>
        {/each}
      </div>

      <button class="btn btn--block" type="button" onclick={addPerson}>Add person</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Deliveries</h2>

      <div class="rows rows--delivery">
        <div class="rows__head" aria-hidden="true">
          <span>Material</span>
          <span>Amount</span>
          <span></span>
        </div>

        {#each deliveries as delivery, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Material</span>
                <!--
                  Picked, not typed, so the admin area's price match cannot be
                  defeated by a spelling. "Something else…" is the escape
                  hatch: a new admix nobody has set up yet still goes on the
                  sheet, and it comes back as an ordinary option next time.
                -->
                <select
                  value={materialValue(index)}
                  onchange={(event) => chooseMaterial(index, (event.currentTarget as HTMLSelectElement).value)}
                  aria-label="Delivery {index + 1} material"
                >
                  <option value="">—</option>
                  {#each materialChoices as name, choice (name)}
                    <option value={String(choice)}>{name}</option>
                  {/each}
                  <option value={OTHER_MATERIAL}>Something else…</option>
                </select>
                {#if customDelivery[index]}
                  <input
                    type="text"
                    bind:value={delivery.material}
                    placeholder="Material name"
                    aria-label="Delivery {index + 1} material name"
                  />
                {/if}
              </label>
              <label class="row__field">
                <span class="label">Amount</span>
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={delivery.amount}
                  onblur={() => (delivery.amount = quickTonnes(delivery.amount))}
                  aria-label="Delivery {index + 1} amount"
                />
              </label>
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveDelivery(index, -1)}
                disabled={index === 0}
                aria-label="Move {delivery.material || `delivery ${index + 1}`} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveDelivery(index, 1)}
                disabled={index === deliveries.length - 1}
                aria-label="Move {delivery.material || `delivery ${index + 1}`} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeDelivery(index)}
                aria-label="Remove {delivery.material || `delivery ${index + 1}`}"
              >✕</button>
            </div>
          </div>
        {/each}
      </div>

      <button class="btn btn--block" type="button" onclick={addDelivery}>Add delivery</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Activities</h2>

      <div class="rows rows--activity">
        <div class="rows__head" aria-hidden="true">
          <span>Piling #</span>
          <span>Unit #</span>
          <span>Start</span>
          <span>Stop</span>
          <span>Amount supplied</span>
          <span>Admixtures used</span>
          <span>Mix design</span>
          <span></span>
        </div>

        {#each activities as activity, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Piling #</span>
                <input
                  type="text"
                  bind:value={activity.piling_number}
                  aria-label="Activity {index + 1} piling number"
                />
              </label>
              <label class="row__field">
                <span class="label">Unit #</span>
                <input
                  type="text"
                  bind:value={activity.unit_number}
                  aria-label="Activity {index + 1} unit number"
                />
              </label>
              <label class="row__field">
                <span class="label">Start</span>
                <input
                  type="text"
                  bind:value={activity.start_time}
                  onblur={() => (activity.start_time = quickTime(activity.start_time))}
                  placeholder="7:00 AM"
                  aria-label="Activity {index + 1} start time"
                />
              </label>
              <label class="row__field">
                <span class="label">Stop</span>
                <input
                  type="text"
                  bind:value={activity.stop_time}
                  onblur={() => (activity.stop_time = quickTime(activity.stop_time))}
                  placeholder="3:30 PM"
                  aria-label="Activity {index + 1} stop time"
                />
              </label>
              <label class="row__field">
                <span class="label">Amount supplied</span>
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={activity.amount_supplied}
                  onblur={() => (activity.amount_supplied = quickCubes(activity.amount_supplied))}
                  aria-label="Activity {index + 1} amount supplied"
                />
              </label>
              <label class="row__field">
                <span class="label">Admixtures used</span>
                <input
                  type="text"
                  bind:value={activity.admixtures_used}
                  aria-label="Activity {index + 1} admixtures used"
                />
              </label>
              <label class="row__field">
                <span class="label">Mix design</span>
                <!--
                  A pick-list from the library, stored as an id for pricing and
                  as the code for the printed sheet (chooseMix). A row saved
                  before the library existed, or with a typed code that matches
                  nothing, shows the blank option and keeps its text — picking
                  the blank option again leaves that text alone too; only
                  picking an actual mix rewrites it.
                -->
                <select
                  value={activity.mix_design_id === null ? '' : String(activity.mix_design_id)}
                  onchange={(event) => chooseMix(index, (event.currentTarget as HTMLSelectElement).value)}
                  aria-label="Activity {index + 1} mix design"
                >
                  <option value="">{activity.water_used && activity.mix_design_id === null ? `(${activity.water_used})` : '—'}</option>
                  {#each mixOptions as option (option.id)}
                    <option value={String(option.id)}>{option.mix_code}{option.project_name ? ` — ${option.project_name}` : ''}</option>
                  {/each}
                </select>
              </label>
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveActivity(index, -1)}
                disabled={index === 0}
                aria-label="Move activity {index + 1} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveActivity(index, 1)}
                disabled={index === activities.length - 1}
                aria-label="Move activity {index + 1} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeActivity(index)}
                aria-label="Remove activity {index + 1}"
              >✕</button>
            </div>
          </div>
        {/each}

        {#if activities.length === 0}
          <p class="meta">No activity rows yet.</p>
        {:else}
          <!--
            The day's concrete, under the Amount supplied column — the same
            `totalCubes` the printed sheet and the admin pours table use, so
            the crew, the PDF and the office all read one number. A row that
            does not read as m³ is left out, and a dash shows when none do.
          -->
          <div class="rows__foot" aria-live="polite">
            <span class="rows__foot-label">Total</span>
            <span class="rows__foot-value">{cubes(totalCubes(activities.map((row) => row.amount_supplied)))}</span>
          </div>
        {/if}
      </div>

      <button class="btn btn--block" type="button" onclick={addActivity}>Add activity</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Other activities</h2>

      <div class="rows rows--delivery">
        <div class="rows__head" aria-hidden="true">
          <span>Activity</span>
          <span>How many</span>
          <span></span>
        </div>

        {#each otherActivities as other, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Activity</span>
                <!--
                  Picked, not typed, so the admin's activity rate matches by
                  name. The rate itself is never sent to this screen; the
                  office P&L multiplies how many by it.
                -->
                <select
                  value={otherValue(index)}
                  onchange={(event) => chooseOther(index, (event.currentTarget as HTMLSelectElement).value)}
                  aria-label="Other activity {index + 1}"
                >
                  <option value="">—</option>
                  {#each otherChoices as option, choice (option.name)}
                    <option value={String(choice)}>{option.name}</option>
                  {/each}
                  <option value={OTHER_ACTIVITY}>Something else…</option>
                </select>
                {#if customOther[index]}
                  <input
                    type="text"
                    bind:value={other.activity}
                    placeholder="Activity name"
                    aria-label="Other activity {index + 1} name"
                  />
                {/if}
              </label>
              <label class="row__field">
                <span class="label">{otherUnitHint(index)}</span>
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={other.quantity}
                  placeholder={otherUnitHint(index) === 'Hours' ? 'e.g. 1.5' : 'e.g. 1'}
                  aria-label="Other activity {index + 1} {otherUnitHint(index).toLowerCase()}"
                />
              </label>
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeOther(index)}
                aria-label="Remove {other.activity || `other activity ${index + 1}`}"
              >✕</button>
            </div>
          </div>
        {/each}

        {#if otherActivities.length === 0}
          <p class="meta">Standby, delivery, small load fee — add one if the day had any.</p>
        {/if}
      </div>

      <button class="btn btn--block" type="button" onclick={addOther}>Add other activity</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Photos</h2>

      {#if photos.length > 0}
        <ul class="phgrid">
          {#each photos as photo (photo.id)}
            <li class="phtile">
              <!-- Full size in a new tab, not a lightbox — same reasoning as
                   the pour record's PhotoField. -->
              <a href={apiPath.dailyReportPhotoContent(photo.id)} target="_blank" rel="noopener noreferrer">
                <img
                  src={apiPath.dailyReportPhotoContent(photo.id)}
                  alt={photo.caption || 'Attached photo'}
                  loading="lazy"
                  decoding="async"
                />
              </a>
              {#if confirmingPhotoRemove === photo.id}
                <div class="phacts">
                  <button class="btn btn--sm btn--danger" type="button" onclick={() => removePhoto(photo.id)}>
                    Remove
                  </button>
                  <button class="btn btn--sm" type="button" onclick={() => (confirmingPhotoRemove = null)}>
                    Keep
                  </button>
                </div>
              {:else}
                <div class="phacts">
                  <button
                    class="btn btn--sm btn--ghost"
                    type="button"
                    onclick={() => (confirmingPhotoRemove = photo.id)}
                    aria-label="Remove this photo"
                  >✕ Remove</button>
                </div>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}

      <div class="captures">
        <label class="capture" class:busy={photoBusy}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            disabled={photoBusy}
            onchange={onPhotoFiles}
          />
          <span>{photoBusy ? 'Preparing…' : '📷 Take photo'}</span>
        </label>
        <!-- Identical input, minus `capture`: that one attribute is the whole
             difference between "open the camera" and "open the photo picker". -->
        <label class="capture" class:busy={photoBusy}>
          <input type="file" accept="image/*" multiple disabled={photoBusy} onchange={onPhotoFiles} />
          <span>{photoBusy ? 'Preparing…' : '🖼️ Choose from gallery'}</span>
        </label>
      </div>

      {#if photoProblem}
        <p class="phproblem" role="status">{photoProblem}</p>
      {/if}
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <label class="field">
        <span class="label">Notes</span>
        <textarea rows="5" bind:value={notes}></textarea>
      </label>

      <div class="grid2">
        <label class="field">
          <span class="label">Prepared by</span>
          <input type="text" bind:value={preparedBy} />
        </label>
        <label class="field">
          <span class="label">Reviewed by</span>
          <input type="text" bind:value={reviewedBy} />
        </label>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <button class="btn btn--block" type="button" disabled={printing} onclick={print}>
        {#if printing}<span class="spinner" aria-hidden="true"></span> Preparing…{:else}Print / Save as PDF{/if}
      </button>

      <button class="btn btn--primary btn--block btn--big" type="button" disabled={saving} onclick={() => save()}>
        {#if saving}<span class="spinner" aria-hidden="true"></span> Saving…{:else}Save daily report{/if}
      </button>

      <!-- The auto-save's word on where things stand. Only ever informational:
           the button above still saves on demand. -->
      <p class="autosave" role="status">
        {#if saving}
          Saving…
        {:else if savedId !== null && !dirty}
          All changes saved.
        {:else if savedId !== null || hasContent()}
          Unsaved changes — saving automatically…
        {:else}
          Saves automatically as you fill it in.
        {/if}
      </p>

      {#if $isAdmin && savedId !== null}
        {#if confirmingDelete}
          <div class="banner banner--warn" role="status">
            <div class="spacer">
              <strong>Delete this daily report?</strong>
              This cannot be undone.
              <div class="banner__actions">
                <button class="btn btn--sm btn--danger" type="button" disabled={deleting} onclick={remove}>
                  {#if deleting}Deleting…{:else}Delete{/if}
                </button>
                <button class="btn btn--sm" type="button" onclick={() => (confirmingDelete = false)}>
                  Keep it
                </button>
              </div>
            </div>
          </div>
        {:else}
          <button class="btn btn--block" type="button" onclick={() => (confirmingDelete = true)}>
            Delete daily report
          </button>
        {/if}
      {/if}
    </div>
  </div>
{/if}
</div>

<style>
  /* ---- photos — the same look PhotoField gives the pour record --------- */
  .phgrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(6.5rem, 1fr));
    gap: 0.5rem;
    margin: 0 0 0.7rem;
    padding: 0;
    list-style: none;
  }

  .phtile {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--paper);
    overflow: hidden;
  }

  .phtile a {
    display: block;
    line-height: 0;
  }

  .phtile img {
    display: block;
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    background: var(--bg-sunken);
  }

  .phacts {
    display: flex;
    gap: 0.25rem;
    padding: 0 0.25rem 0.25rem;
  }

  .phacts .btn {
    flex: 1 1 auto;
  }

  /* Camera and gallery side by side where there is room; stacked full-width
     rows on a narrow phone rather than squeezing both labels. */
  .captures {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* The capture control is the label; the input itself is never seen. */
  .capture {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 12rem;
    min-height: var(--tap);
    padding: 0.75rem 1rem;
    font-size: 1rem;
    font-weight: 700;
    background: var(--bg-sunken);
    border: 2px dashed var(--line);
    border-radius: var(--radius);
    cursor: pointer;
    touch-action: manipulation;
  }

  .capture:focus-within {
    border-color: var(--brand);
    box-shadow: 0 0 0 3px var(--brand-tint);
  }

  .capture.busy {
    opacity: 0.7;
  }

  .capture input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .phproblem {
    margin: 0.5rem 0 0;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--hold);
  }

  /* Informational, never alarming: where the auto-save stands. */
  .autosave {
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    color: var(--ink-faint);
    text-align: center;
  }

  .titlebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    margin-bottom: 0.6rem;
  }

  .titlebar .card__title {
    margin: 0;
  }

  /*
    Shared structure for all three tables (personnel, deliveries,
    activities), copied directly from MixDesignForm's materials grid: below
    60rem every field in a row stacks into its own labelled block. At and
    above 60rem each table switches to one line per row under a single set
    of column headings, sharing ONE column template string — set once per
    table as --rowcols on the .rows wrapper — between .rows__head and every
    .row, exactly as --matcols is shared there. .row__fields then drops out
    of layout via display:contents so its labelled children join the row's
    own grid directly as columns 1..N, with .row__actions taking the final
    column; nothing here reads --rowcols itself except .rows__head and
    .row, so the two can never drift out of alignment with each other the
    way two independently-sized grids could.

    The activity table is the one this whole approach exists to protect:
    with 7 data columns plus the 3-button action column, its --rowcols
    floors are deliberately narrow and every data column is a
    minmax(...,1fr) rather than a fixed width, so the row can always shrink
    toward its floor instead of forcing an overflow. A minmax() floor is a
    hard minimum — CSS Grid will NOT shrink a track below it even when the
    container is narrower, so getting the floor total wrong here means a
    real, measurable side-scroll, not just a cramped one. This app sets
    `html { font-size: 17px }` (see app.css), which is what every rem below
    resolves against in normal layout, so a naive "1rem = 16px" estimate
    undercounts every floor by 6.25% — that mistake shipped once already and
    was only caught by measuring actual computed widths in a real browser at
    the exact 60rem breakpoint width (960px, which is fixed at 16px per the
    CSS Values spec regardless of the root's own font-size): the floors plus
    gaps plus the action column summed to ~893px against only ~875px of
    width actually granted to the row there, an 18px real overflow. The
    floors below were re-measured the same way and now sum, with gaps and
    the action column, to ~791px against that same ~875px — about 80px of
    slack at the tightest point, growing further above the breakpoint and
    up to the 64rem/1088px office container ceiling.
  */
  .rows--person {
    --rowcols: minmax(9rem, 1fr) 7rem 7rem calc(3 * var(--tap) + 0.6rem);
  }

  .rows--delivery {
    --rowcols: minmax(10rem, 1fr) 8rem calc(3 * var(--tap) + 0.6rem);
  }

  .rows--activity {
    --rowcols:
      minmax(5rem, 1fr) minmax(5rem, 1fr) minmax(4rem, 1fr) minmax(4rem, 1fr)
      minmax(5.5rem, 1fr) minmax(5.5rem, 1fr) minmax(4.5rem, 1fr) calc(3 * var(--tap) + 0.6rem);
  }

  .row__fields {
    display: grid;
    gap: 0.6rem;
  }

  .row__field {
    display: block;
    min-width: 0;
  }

  /* The delivery free-text box, revealed under the pick-list it came from. */
  .row__field select + input {
    margin-top: 0.4rem;
  }

  .rows__head {
    display: none;
  }

  /*
    The total under Activities. Stacked on a phone it is one line, label then
    number; on the grid it takes the row's own columns so the number lands
    under Amount supplied, the fifth column, with the label filling the four
    before it.
  */
  .rows__foot {
    display: flex;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.6rem 0 0;
    border-top: 2px solid var(--line);
    font-weight: 700;
  }

  .rows__foot-value {
    font-variant-numeric: tabular-nums;
  }

  .rows__foot-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--ink-soft);
    align-self: center;
  }

  .row {
    border-bottom: 1px solid var(--line-soft);
    padding: 0.85rem 0;
  }

  .row:first-child {
    padding-top: 0;
  }

  .row:last-of-type {
    border-bottom: 0;
  }

  .row__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.3rem;
    margin-top: 0.6rem;
  }

  @media (min-width: 60rem) {
    .rows__head {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      padding: 0 0 0.4rem;
      border-bottom: 2px solid var(--line);
    }

    .rows__head span {
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--ink-soft);
    }

    .rows__foot {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      align-items: center;
    }

    .rows__foot-label {
      grid-column: 1 / 5;
    }

    .rows__foot-value {
      grid-column: 5;
    }

    .row {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      align-items: center;
      padding: 0.4rem 0;
    }

    /* The wrapper drops out of layout so its children take their place
       directly as the row's own leading columns, in the document order
       they already had — same mechanism as MixDesignForm's
       .material__fields. */
    .row__fields {
      display: contents;
    }

    .row__field .label {
      display: none;
    }

    .row__actions {
      justify-content: flex-start;
      margin-top: 0;
    }
  }
</style>
